import React from "react";
import { useQuery } from "@tanstack/react-query";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from "recharts";
import {
  Banknote, FileText, AlertCircle, Users, CreditCard, BarChart2, Landmark,
} from "lucide-react";
import { getPayrollSummary } from "../api";
import { useTenantTimezone } from "../hooks/useTenantTimezone";
import { formatDate } from "../utils/dateFormat";
import { StatCard, QuickAction } from "../components/DashboardWidgets";
import ModuleSetupCard from "../components/ModuleSetupCard";

const NAVY   = "#1e3a8a";
const TEAL   = "#0f766e";
const AMBER  = "#f59e0b";
const RED    = "#dc2626";
const PURPLE = "#7c3aed";
const GREEN  = "#16a34a";

const MONTH_NAMES = ["January", "February", "March", "April", "May", "June",
                     "July", "August", "September", "October", "November", "December"];

// Payroll module's dashboard — module home for /payroll-home (spec:
// payroll-dashboard). Replaces the single-card generic ModuleDashboardPage
// entry: same shared widgets, plus the cost/coverage stats and the 6-month
// trend that the generic page had no data for.
export default function PayrollDashboardPage() {
  const tz = useTenantTimezone();
  const { data: summary } = useQuery({
    queryKey: ["payroll-summary"],
    queryFn: getPayrollSummary,
    refetchInterval: 60_000,
  });

  const sym = summary?.currency_symbol ?? "";
  const money = (n: number | undefined) =>
    `${sym}${(n ?? 0).toLocaleString("en", { maximumFractionDigits: 0 })}`;

  const covered = summary?.employees_paid_this_month ?? 0;
  const headcount = summary?.active_employees ?? 0;
  const missing = summary?.missing_salary_structure ?? 0;
  const unpaidCount = summary?.unpaid_issued_count ?? 0;
  const monthName = MONTH_NAMES[new Date().getMonth()];

  return (
    <div>
      {/* No width cap: the module's own list pages (Candidates, Jobs, Offers,
          Interview Hub) all run the full content width, so capping the module
          landing page made the layout jump as you moved between them. */}

      {/* No "MODULE" eyebrow — same call as ModuleDashboardPage: the sidebar
          badge and page header already name the module. */}
      <div style={{ marginBottom: 22 }}>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: "#0f172a", letterSpacing: -0.5 }}>
          Payroll
        </h1>
        {summary?.last_run_at && (
          <p style={{ margin: "4px 0 0", color: "#64748b", fontSize: 13 }}>
            Last run {formatDate(new Date(summary.last_run_at), "long", tz)}
            {summary.last_run_by ? ` by ${summary.last_run_by}` : ""}
          </p>
        )}
      </div>

      <ModuleSetupCard module="payroll" />

      {/* ── Stat cards ── */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 14, marginBottom: 28 }} className="stat-cards-row">
        <StatCard label="Payroll Cost This Month" value={money(summary?.net_this_month)}
          color={NAVY} icon={<Banknote size={18} />} to="/payroll"
          subtitle={`Net pay · ${monthName}`} />
        <StatCard label="Draft Payslips" value={summary?.draft_slips ?? 0}
          color={TEAL} icon={<FileText size={18} />} to="/payroll" subtitle="Not yet issued" />
        <StatCard label="Issued But Unpaid" value={unpaidCount}
          color={unpaidCount > 0 ? RED : TEAL} icon={<AlertCircle size={18} />}
          to="/payroll?filter=unpaid"
          subtitle={unpaidCount > 0 ? `${money(summary?.unpaid_issued_amount)} to pay` : "All settled"} />
        <StatCard label="Run Coverage" value={`${covered} / ${headcount}`}
          color={headcount > 0 && covered >= headcount ? GREEN : AMBER}
          icon={<Users size={18} />} to="/payroll"
          subtitle="Employees with a slip this month" />
        <StatCard label="Missing Salary Structure" value={missing}
          color={missing > 0 ? AMBER : TEAL} icon={<AlertCircle size={18} />}
          to="/payroll/structure"
          subtitle={missing > 0 ? "Active employees without one" : "All employees covered"} />
        <StatCard label="Tax Withheld This Month" value={money(summary?.tax_this_month)}
          color={PURPLE} icon={<Landmark size={18} />} to="/payroll/exports"
          subtitle={monthName} />
      </div>

      {/* ── Quick actions ── */}
      <div style={{
        background: "white", borderRadius: 10, padding: "20px 24px",
        border: "1px solid #e2e8f0", boxShadow: "0 1px 3px rgba(0,0,0,0.06)", marginBottom: 24,
      }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: "#0f172a", marginBottom: 14 }}>Quick Actions</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
          <QuickAction label="Run Payroll" icon={<Banknote size={14} />} to="/payroll" color={TEAL} />
          <QuickAction label="Issue All Drafts" icon={<FileText size={14} />} to="/payroll" color={NAVY} />
          <QuickAction label="Bank Transfer File" icon={<CreditCard size={14} />} to="/payroll/exports" color={PURPLE} />
          <QuickAction label="PND.1 Export" icon={<FileText size={14} />} to="/payroll/exports" color={RED} />
          <QuickAction label="SSO Export" icon={<FileText size={14} />} to="/payroll/exports" color={AMBER} />
          <QuickAction label="Annual Summary" icon={<BarChart2 size={14} />} to="/payroll/exports" color={NAVY} />
          <QuickAction label="Payroll Reports" icon={<BarChart2 size={14} />} to="/reports" color={TEAL} />
        </div>
      </div>

      {/* ── Cost trend ── */}
      <div style={{
        background: "white", borderRadius: 10, padding: "22px 24px",
        border: "1px solid #e2e8f0", boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
      }}>
        <div style={{ marginBottom: 18 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: "#0f172a" }}>Payroll Cost Trend</div>
          <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
            Net pay + employer contributions · last 6 months
          </div>
        </div>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={(summary?.trend ?? []) as any[]} barSize={18} barGap={4}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
            <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} width={44}
              tickFormatter={(v: number) => v >= 1000 ? `${Math.round(v / 1000)}k` : String(v)} />
            <Tooltip cursor={{ fill: "#f8fafc" }}
              formatter={(v: number) => `${sym}${v.toLocaleString("en", { maximumFractionDigits: 0 })}`} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar dataKey="net" name="Net Pay" fill={TEAL} radius={[4, 4, 0, 0]} />
            <Bar dataKey="employer" name="Employer Contributions" fill={NAVY} radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

    </div>
  );
}
